import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { HiOutlineUsers, HiOutlineLockClosed, HiX } from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import UserAvatar from "../../../atoms/UserAvatar";
import NoTranslate from "../../../atoms/NoTranslate";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useSaveTodoBoardAccess } from "../../../../hooks/todos";
import { useResolveAssignees } from "../../../../hooks/todos/useResolveAssignees";
import {
  BoardAccess,
  TodoAccessMode,
  normalizeBoardAccess,
} from "../../../../types/TodoTypes";
import PersonFilter from "./PersonFilter";

interface Props {
  show: boolean;
  onClose: () => void;
  boardId: string;
  boardName: string;
  access?: BoardAccess;
}

const BoardAccessModal: React.FC<Props> = ({
  show,
  onClose,
  boardId,
  boardName,
  access,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const entityId = user.entity_identification;
  const canShare = permissions.can(Cap.TASKS_BOARDS, Level.DELETE);
  const currentUserId = user.user_identification || "";

  const save = useSaveTodoBoardAccess(customer_identification, entityId);

  const [userIds, setUserIds] = useState<string[]>([]);

  const isRestricted = userIds.length > 0;
  const mode: TodoAccessMode = isRestricted ? "restricted" : "everyone";

  /**
   * La personne qui edite ne peut pas se retirer : le back la reinsere de force,
   * et une interface qui laisserait croire l'inverse mentirait sur le resultat.
   */
  const withSelf = useCallback(
    (ids: string[]) =>
      currentUserId && !ids.includes(currentUserId)
        ? [currentUserId, ...ids]
        : ids,
    [currentUserId]
  );

  useEffect(() => {
    if (!show) return;
    const normalized = normalizeBoardAccess(access);
    setUserIds(
      normalized.mode === "restricted" ? withSelf(normalized.user_ids) : []
    );
  }, [show, boardId]);

  const { resolvedUsers, isResolving } = useResolveAssignees(
    customer_identification,
    entityId,
    mode === "restricted" ? userIds : [],
    []
  );

  const rows = useMemo(
    () =>
      userIds.map((id) => ({
        id,
        person: resolvedUsers[id],
        isSelf: id === currentUserId,
      })),
    [userIds, resolvedUsers, currentUserId]
  );

  const handleSave = () => {
    if (!canShare || save.isLoading) return;
    const payload =
      mode === "restricted"
        ? { mode, user_ids: withSelf(userIds) }
        : { mode, user_ids: [] as string[] };
    save.mutate(
      { boardId, ...payload },
      {
        onSuccess: () => {
          toast.success(t("todos.access.saved") as string);
          onClose();
        },
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message ||
              t("todos.access.saveError")) as string
          );
        },
      }
    );
  };

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      dismissible
      title={t("todos.access.title")}
      subtitle={boardName}
      footer={
        <>
          <CustomButton
            variant="secondary"
            title={t("common.cancel") as string}
            onClick={onClose}
          />
          <CustomButton
            variant="primary"
            isLoading={save.isLoading}
            disabled={save.isLoading || !canShare}
            title={t("common.saveShort") as string}
            onClick={handleSave}
          />
        </>
      }
    >
      <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
        {isRestricted ? (
          <HiOutlineLockClosed
            size={18}
            className="mt-0.5 shrink-0 text-gray-500"
          />
        ) : (
          <HiOutlineUsers size={18} className="mt-0.5 shrink-0 text-gray-500" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {isRestricted
              ? t("todos.access.stateRestricted", { count: userIds.length })
              : t("todos.access.stateEveryone")}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            {isRestricted
              ? t("todos.access.restrictedHint")
              : t("todos.access.startHint")}
          </p>
        </div>
      </div>

      {canShare && (
        <div className="mt-5">
          <h4 className="mb-1.5 text-sm font-semibold text-gray-900">
            {t("todos.access.addPerson")}
          </h4>
          <PersonFilter
            key={`${boardId}-${show}`}
            inline
            customerId={customer_identification}
            entityId={entityId}
            selectedIds={userIds}
            showCount={false}
            onAdd={(person) => {
              if (!person.id || userIds.includes(person.id)) return;
              setUserIds((prev) => withSelf([...prev, person.id]));
            }}
          />
          <p className="mt-1 text-xs text-gray-400">
            {t("todos.access.searchHint")}
          </p>
        </div>
      )}

      <div className="mt-4 border-t border-gray-100 pt-4">
        <h4 className="text-sm font-semibold text-gray-900">
          {t("todos.access.peopleTitle")}
          {isRestricted && (
            <span className="ml-1.5 text-xs font-normal tabular-nums text-gray-500">
              <NoTranslate>{userIds.length}</NoTranslate>
            </span>
          )}
        </h4>

        {isRestricted && (
          <p className="mt-1.5 text-xs text-gray-500">
            {t("todos.access.selfKeptHint")}
          </p>
        )}

        <div className="mt-3 space-y-1.5">
          {rows.length === 0 && (
            <p className="py-4 text-center text-sm text-gray-400">
              {t("todos.access.empty")}
            </p>
          )}
          {rows.map(({ id, person, isSelf }) => (
            <div
              key={id}
              className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2"
            >
              <UserAvatar
                firstName={person?.firstName}
                lastName={person?.lastName}
                email={person?.email}
                avatar={person?.avatar}
                sizeClass="w-8 h-8"
                textClass="text-xs"
              />
              <div className="min-w-0 flex-1">
                <div
                  className={`truncate text-sm font-medium ${
                    person?.name ? "text-gray-800" : "text-gray-400 italic"
                  }`}
                >
                  <NoTranslate>
                    {person?.name ||
                      (isResolving
                        ? (t("todos.filters.resolving") as string)
                        : (t("todos.filterPerson.unknown") as string))}
                  </NoTranslate>
                </div>
                {person?.email && (
                  <div className="truncate text-xs text-gray-400">
                    <NoTranslate>{person.email}</NoTranslate>
                  </div>
                )}
              </div>
              {isSelf ? (
                <span className="shrink-0 text-xs text-gray-400">
                  {t("todos.access.you")}
                </span>
              ) : (
                canShare && (
                  <CustomButton
                    type="button"
                    variant="none"
                    size="icon"
                    onClick={() =>
                      setUserIds((prev) => prev.filter((x) => x !== id))
                    }
                    className="shrink-0 rounded-md text-gray-400 enabled:hover:text-red-600"
                    icon={<HiX size={16} />}
                    aria-label={t("todos.access.removePerson") as string}
                  />
                )
              )}
            </div>
          ))}
        </div>

        {isRestricted && canShare && (
          <button
            type="button"
            onClick={() => setUserIds([])}
            className="mt-3 text-xs text-gray-500 underline hover:text-gray-700"
          >
            {t("todos.access.openToTeam")}
          </button>
        )}
      </div>
    </ModalShell>
  );
};

export default BoardAccessModal;
